/**
 * The information architecture (docs/ux/review/navigation-ia.md §3, docs/ux/artboards/optimal):
 * the tab strip is the navigation. Three top-level places:
 *
 *   /home[?v=needs|running|projects][&project=…][&i=kind:id]   Home (tab 0, ⌥0)
 *   /p/$id[/goal|/history|/releases|/artifacts|/settings]          a project tab (⌥1–9) and its views
 *   /p/$id/s/$runId[?seq=N|at=kind:id]                          the run, in the project's chrome
 *   /p/$id/w/$wsId  /p/$id/t/$taskId                            a workstream / task, popped out
 *   /settings[/$section]                                        Settings (You + Rules + Research)
 *
 * Everything scoped to one project lives under `/p/$id`. This module holds the pure URL grammar:
 * canonical builders, the current project of a path, and where every old URL goes now. It never
 * touches the router, so it is table-tested (test/ia.test.ts).
 */
import { LEGACY_ANCHORS } from '../routes/you/sections.js'

export type HomeView = 'needs' | 'running' | 'projects'
export const HOME_VIEWS: readonly HomeView[] = ['needs', 'running', 'projects']

/** Views inside a project tab, in strip order (`''` is Lanes, the tab's default body). */
export type ProjectView = '' | 'goal' | 'history' | 'releases' | 'artifacts' | 'settings'
export const PROJECT_VIEWS: readonly { id: ProjectView; label: string; keys: string }[] = [
  { id: '', label: 'Lanes', keys: 'g l' },
  // fidelity-project: the goal's orchestration tree (app-clarity ide/goal concepts).
  { id: 'goal', label: 'Orchestration', keys: 'g t' },
  { id: 'history', label: 'History', keys: 'g h' },
  { id: 'releases', label: 'Builds', keys: 'g b' },
  { id: 'artifacts', label: 'Artifacts', keys: 'g a' },
  { id: 'settings', label: 'Settings', keys: ',' },
]

export const SETTINGS_SECTIONS = [
  { id: 'machines', label: 'Machines', blurb: 'Status, load, doctor, branches' },
  { id: 'providers', label: 'Providers and usage', blurb: 'Accounts, usage, cost' },
  { id: 'rules', label: 'Rules', blurb: 'What every agent is told' },
  { id: 'research', label: 'Research library', blurb: 'Research you keep, voice mode' },
  { id: 'notifications', label: 'Notifications', blurb: 'Push, quiet hours, digests' },
  { id: 'appearance', label: 'Appearance and keys', blurb: 'Theme, status line' },
  { id: 'security', label: 'Security', blurb: 'Passkeys, trusted devices, app lock, recovery' },
  { id: 'account', label: 'Account', blurb: 'Email, invites, sign out' },
  { id: 'advanced', label: 'Advanced', blurb: 'Automation grants, project autonomy' },
] as const
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]['id']

const enc = encodeURIComponent

export function homeHref(o: { v?: HomeView; project?: string; i?: string } = {}): string {
  const q = new URLSearchParams()
  if (o.v) q.set('v', o.v)
  if (o.project) q.set('project', o.project)
  if (o.i) q.set('i', o.i)
  const s = q.toString()
  return s ? `/home?${s}` : '/home'
}

export function projectHref(projectId: string, view: ProjectView = '', i?: string): string {
  const base = `/p/${enc(projectId)}${view ? `/${view}` : ''}`
  return i ? `${base}?i=${enc(i)}` : base
}

/** The canonical run URL: in its project's chrome, or the short link when the project is unknown. */
export function runHref(runId: string, projectId?: string, seq?: number): string {
  const base = projectId ? `/p/${enc(projectId)}/s/${enc(runId)}` : `/s/${enc(runId)}`
  return seq && seq > 0 ? `${base}?seq=${seq}` : base
}

/** A workstream (or task) popped out of the inspector to full width, inside its project's tab. */
export function workstreamHref(workstreamId: string, projectId: string): string {
  return `/p/${enc(projectId)}/w/${enc(workstreamId)}`
}
export function taskHref(taskId: string, projectId: string): string {
  return `/p/${enc(projectId)}/t/${enc(taskId)}`
}

export function settingsHref(section?: SettingsSection | 'enroll'): string {
  return section ? `/settings/${section}` : '/settings'
}

/** The project a path belongs to (`/p/$id…`), else undefined. */
export function projectOfPath(pathname: string): string | undefined {
  const m = /^\/p\/([^/?#]+)/.exec(pathname)
  if (!m?.[1]) return undefined
  try {
    return decodeURIComponent(m[1])
  } catch {
    return undefined
  }
}

/** The view of a project path (`/p/$id/history` → history), else undefined. */
export function projectViewOfPath(pathname: string): ProjectView | 'run' | 'work' | undefined {
  const m = /^\/p\/[^/]+(?:\/([^/?#]+))?/.exec(pathname)
  if (!m) return undefined
  if (!m[1]) return ''
  if (m[1] === 's') return 'run'
  if (m[1] === 'w' || m[1] === 't') return 'work'
  return PROJECT_VIEWS.some((v) => v.id === m[1]) ? (m[1] as ProjectView) : undefined
}

// ---- old URLs ------------------------------------------------------------------------------------

/**
 * Where an old URL goes. `to` is final; `resolve` needs the entity's project first (the redirect
 * component looks it up in cached data, then builds the URL with `resolved`).
 */
export type LegacyTarget =
  | { to: string }
  | { resolve: 'run'; id: string; seq?: number; fallback: string }
  | { resolve: 'workstream'; id: string; fallback: string }
  | { resolve: 'task'; id: string; fallback: string }
  | { resolve: 'inspect'; key: string; fallback: string }
  | { resolve: 'lastProject'; view: ProjectView; fallback: string }

/** Old `/projects/$id?tab=x` views → the tab's views. */
const TAB_VIEW: Record<string, ProjectView> = {
  board: '',
  history: 'history',
  changes: 'history',
  import: 'history',
  landing: 'releases',
  artifacts: 'artifacts',
  research: 'artifacts',
  feedback: 'artifacts',
  settings: 'settings',
}

const YOU_SECTION: Record<string, SettingsSection> = {
  account: 'account',
  security: 'security',
  machines: 'machines',
  providers: 'providers',
  notifications: 'notifications',
  appearance: 'appearance',
  advanced: 'advanced',
  research: 'research',
}

function keep(search: URLSearchParams, names: readonly string[]): URLSearchParams {
  const out = new URLSearchParams()
  for (const n of names) {
    const v = search.get(n)
    if (v !== null) out.set(n, v)
  }
  return out
}
const withQuery = (path: string, q: URLSearchParams, hash = '') => {
  const s = q.toString()
  return `${path}${s ? `?${s}` : ''}${hash}`
}

/**
 * Maps a pre-IA URL to its new home, preserving deep links (`?i=`, `?intent=`, `?seq=`, `#anchor`).
 * Returns undefined for URLs that are already canonical (or not ours).
 */
export function legacyTarget(
  pathname: string,
  rawSearch = '',
  hash = '',
): LegacyTarget | undefined {
  const search = new URLSearchParams(rawSearch)
  const parts = pathname.replace(/\/+$/, '').split('/').slice(1).map(safeDecode)
  const [head, a] = parts
  const seq = Number(search.get('seq'))
  const seqOpt = Number.isSafeInteger(seq) && seq > 0 ? { seq } : {}
  const inspect = search.get('i') ?? undefined
  switch (head) {
    case 'now':
    case 'inbox': {
      const v: HomeView = head === 'now' ? 'running' : 'needs'
      if (a) return { resolve: 'run', id: a, ...seqOpt, fallback: runHref(a, undefined, seq) }
      const fallback = withQuery(
        '/home',
        new URLSearchParams([['v', v], ...keep(search, ['i', 'intent', 'project'])]),
      )
      // A decision or ask opens inside its project's tab (push deep links).
      if (inspect) return { resolve: 'inspect', key: inspect, fallback }
      return { to: fallback }
    }
    case 'overview':
      return { to: '/home?v=projects' }
    case 'projects': {
      if (!a) return { to: '/home?v=projects' }
      const view = TAB_VIEW[search.get('tab') ?? 'board'] ?? ''
      return { to: withQuery(projectHref(a, view), keep(search, ['i'])) }
    }
    case 'w':
      return a ? { resolve: 'workstream', id: a, fallback: '/home?v=projects' } : undefined
    case 't':
      return a ? { resolve: 'task', id: a, fallback: '/home?v=projects' } : undefined
    case 's':
      return a
        ? { resolve: 'run', id: a, ...seqOpt, fallback: runHref(a, undefined, seq) }
        : undefined
    case 'you': {
      if (a === 'enroll') return { to: withQuery('/settings/enroll', search) }
      const section = a ? YOU_SECTION[a] : undefined
      if (a && !section) return { to: '/settings' }
      // Old single-page anchors (`/you#machines`) land on the section that holds them.
      const anchored = !a ? LEGACY_ANCHORS[hash.replace(/^#/, '')] : undefined
      const to = section ?? anchored
      return { to: withQuery(to ? `/settings/${to}` : '/settings', search, hash) }
    }
    case 'rules':
      return { to: '/settings/rules' }
    case 'releases':
      return { resolve: 'lastProject', view: 'releases', fallback: '/home?v=projects' }
    default:
      return undefined
  }
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s)
  } catch {
    return s
  }
}

/** The final URL of a resolved legacy target. */
export function resolvedTarget(
  t: Exclude<LegacyTarget, { to: string }>,
  projectId: string | undefined,
): string {
  if (!projectId) return t.fallback
  switch (t.resolve) {
    case 'run':
      return runHref(t.id, projectId, t.seq)
    case 'workstream':
      return workstreamHref(t.id, projectId)
    case 'task':
      return taskHref(t.id, projectId)
    case 'inspect':
      return projectHref(projectId, '', t.key)
    case 'lastProject':
      return projectHref(projectId, t.view)
  }
}
